import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { TaskDocument } from '@/lib/db/rxdb/schemas/task'
import { useTaskMutations } from '@/lib/db/rxdb/hooks'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { useTaskRowActionsStore } from '@/store/TaskRowActionsStore'
import { useTaskSelectionStore } from '@/store/TaskSelectionStore'

function isTyping(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

/** The row under the pointer, or failing that the focused row. */
function targetRowId(): string | null {
  const hovered = useTaskRowActionsStore.getState().hoveredId
  if (hovered) return hovered
  const focused = document.activeElement?.closest<HTMLElement>('[data-task-id]')
  return focused?.dataset.taskId ?? null
}

/**
 * Row keys (design 23): X selects, D date, A assign, M move, ⌘⌫ delete.
 * The ⋯ menu lists the same keys, so hover never has to show every action.
 */
export function useTaskRowKeys(items: ReadonlyArray<TaskDocument>) {
  const { t } = useTranslation('tasks')
  const { deleteTask, updateTask } = useTaskMutations()
  const canDelete = useHasPermission(WORKSPACE_PERMISSIONS.DELETE_TASKS)
  const itemsRef = useRef(items)
  itemsRef.current = items

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || isTyping(event.target)) return
      if (document.querySelector('[role="dialog"][data-state="open"], [role="menu"]')) return
      const id = targetRowId()
      if (!id || !itemsRef.current.some((task) => task.id === id)) return

      const mod = event.metaKey || event.ctrlKey
      if (mod && event.key === 'Backspace') {
        if (!canDelete) return
        event.preventDefault()
        void deleteTask(id).then(() => {
          toast(t('rowKeys.deleted'), {
            action: {
              label: t('rowKeys.undo'),
              onClick: () => void updateTask(id, { isDeleted: false, deletedOn: null, deletedBy: null }),
            },
          })
        })
        return
      }
      if (mod || event.altKey || event.shiftKey) return

      const key = event.key.toLowerCase()
      if (key === 'x') {
        event.preventDefault()
        useTaskSelectionStore.getState().toggle(id)
        return
      }
      const menu = key === 'd' ? 'date' : key === 'a' ? 'assign' : key === 'm' ? 'move' : null
      if (menu) {
        event.preventDefault()
        useTaskRowActionsStore.getState().setOpenMenu({ taskId: id, menu })
      }
    }

    // One listener per mounted list; each acts only on its own rows, and a
    // row lives in exactly one list, so grouped pages don't double-fire.
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [canDelete, deleteTask, t, updateTask])
}
